import { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Text, Theme } from "@radix-ui/themes";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  maxWidth?: number;
  children: ReactNode;
}

// Sits above SideDrawer (z-index 10000). Use when a dialog is opened from
// inside a drawer / side-panel; the default Radix Themes Dialog stacks below.
export default function HighZIndexDialog({
  open,
  onOpenChange,
  title,
  description,
  maxWidth = 720,
  children,
}: Props) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 10001,
            }}
          />
          <Dialog.Content
            aria-describedby={undefined}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: `min(${maxWidth}px, calc(100vw - 32px))`,
              maxHeight: "calc(100vh - 64px)",
              overflowY: "auto",
              background: "var(--color-panel-solid)",
              borderRadius: "var(--radius-4)",
              padding: 24,
              boxShadow: "var(--shadow-5)",
              zIndex: 10002,
              contain: "none",
            }}
          >
            <Dialog.Title asChild>
              <Text as="div" size="4" weight="bold" mb={description ? "1" : "2"}>
                {title}
              </Text>
            </Dialog.Title>
            {description && (
              <Dialog.Description asChild>
                <Text as="div" size="2" color="gray" mb="3">
                  {description}
                </Text>
              </Dialog.Description>
            )}
            {children}
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
